// Shared text-scan primitives for the keyword-slot classifiers
// (`nextKeywordSlot.ts`, `clauseShapeSlot.ts`).
//
// PURE text scan — no parse, no async, safe on the resolver hot path. Split out
// so the two classifier modules can share one implementation of "where does the
// token before the caret start and end" without either importing the other's
// internals.

export const IDENT_CHAR = /[A-Za-z0-9_]/;

export type WordSpan = { readonly word: string; readonly start: number };

/** True when there is at least one whitespace char between the last
 *  non-whitespace token and the cursor (i.e. the user finished a token). */
export function hasTrailingWhitespace(text: string, offset: number, statementStart: number): boolean {
  const end = Math.min(offset, text.length);
  if (end <= statementStart) return false;
  return /\s/.test(text[end - 1] ?? '');
}

/** Index one past the last non-whitespace char before the cursor. */
export function lastTokenEnd(text: string, offset: number, statementStart: number): number {
  let i = Math.min(offset, text.length);
  while (i > statementStart && /\s/.test(text[i - 1] ?? '')) i -= 1;
  return i;
}

/** Extracts the bareword ending exactly at `end` (no trailing whitespace
 *  skipping). Returns null when the char before `end` is not an identifier
 *  char. */
export function trailingWord(text: string, end: number, statementStart: number): WordSpan | null {
  if (end <= statementStart) return null;
  const prev = text[end - 1] ?? '';
  if (!IDENT_CHAR.test(prev)) return null;
  let j = end;
  while (j > statementStart && IDENT_CHAR.test(text[j - 1] ?? '')) j -= 1;
  return { word: text.slice(j, end), start: j };
}

/** The upper-cased bareword immediately before `end` (whitespace skipped), or
 *  '' when the preceding token is not a bareword. */
export function wordBefore(text: string, end: number, statementStart: number): string {
  let i = end;
  while (i > statementStart && /\s/.test(text[i - 1] ?? '')) i -= 1;
  return trailingWord(text, i, statementStart)?.word.toUpperCase() ?? '';
}

/** Dialect lexing rules the region scan needs. Derived from the engine at the
 *  classifier's entry point and threaded down, because `#` and `$…$` mean
 *  different things per dialect: `#` opens a comment in MySQL but names a temp
 *  table in T-SQL and starts an operator in PG, and `$tag$…$tag$` is a string
 *  literal in PG while `$` is an ordinary identifier char in Oracle. */
export type ScanOptions = {
  readonly hashIsLineComment?: boolean;
  readonly dollarQuotedStrings?: boolean;
  /** PostgreSQL nests block comments; the other dialects end the comment
   *  at the first close. */
  readonly nestedBlockComments?: boolean;
  /** MySQL treats `\` as an escape inside string literals. */
  readonly backslashEscapes?: boolean;
};

export type RegionScan = {
  /** Upper-cased barewords found in CODE — never inside a comment, a string
   *  literal or a delimited identifier. */
  readonly tokens: readonly WordSpan[];
  /** One past the last code character in the region, or `from` when there is
   *  none. Comments and literals count as code for this purpose only when the
   *  caret is meant to sit after them: a `-- note` tail reports the end of the
   *  token BEFORE the comment, which is what "the last thing the user wrote"
   *  means for a keyword decision. */
  readonly lastCodeEnd: number;
  /** The last code character itself (`''` when the region has none) — callers
   *  test it for an operand tail (`)`, a quote, an identifier char). */
  readonly lastCodeChar: string;
  /** Net unclosed `(` at the end of the region, counting only code parens — a
   *  `'('` inside a literal must not open a group. */
  readonly parenDepth: number;
  /** Offset of the innermost still-open `(`, or null when none is open. */
  readonly openParenAt: number | null;
};

/**
 * Single comment- and literal-aware pass over a region.
 *
 * The keyword classifiers decide what to offer from the words the user has
 * written, so a word inside a comment or a string must not count: without this,
 * `SELECT status = 'CASE CLOSED' ▮` reads as an open CASE expression and
 * `-- note about UNION⏎▮` reads as a set operator, and each one confidently
 * replaces the whole suggestion list with a handful of illegal keywords.
 *
 * Delimited identifiers (`"CASE"`, `[UNION]`, `` `IS` ``) are skipped as words
 * for the same reason — quoting is exactly how a user says "this is a name, not
 * a keyword" — but they still register as code for `lastCodeEnd`, since they
 * are operands.
 */
// One classification pass asks ~8 classifiers the same question, and each used
// to re-walk the statement. The scan is pure, so a tiny fixed-size cache turns
// that into one walk per distinct region — which matters because this runs on
// every keystroke. Entries are replaced round-robin; no eviction policy is
// needed for a working set this small.
type CacheEntry = { text: string; from: number; to: number; flags: number; scan: RegionScan };
const SCAN_CACHE_SIZE = 8;
const scanCache: (CacheEntry | undefined)[] = new Array(SCAN_CACHE_SIZE);
let scanCacheNext = 0;

/** The option flags as a bitmask, so cache lookups compare a number. */
function optionFlags(opts: ScanOptions): number {
  return (
    (opts.hashIsLineComment ? 1 : 0) |
    (opts.dollarQuotedStrings ? 2 : 0) |
    (opts.nestedBlockComments ? 4 : 0) |
    (opts.backslashEscapes ? 8 : 0)
  );
}

export function scanRegion(
  text: string,
  from: number,
  to: number,
  opts: ScanOptions = {},
): RegionScan {
  const flags = optionFlags(opts);
  for (const entry of scanCache) {
    if (entry && entry.from === from && entry.to === to && entry.flags === flags && entry.text === text) {
      return entry.scan;
    }
  }
  const scan = computeRegionScan(text, from, to, opts);
  scanCache[scanCacheNext] = { text, from, to, flags, scan };
  scanCacheNext = (scanCacheNext + 1) % SCAN_CACHE_SIZE;
  return scan;
}

/** Test seam — drops the memo so a test cannot observe a stale entry. */
export function __resetScanCacheForTest(): void {
  scanCache.fill(undefined);
  scanCacheNext = 0;
}

function computeRegionScan(
  text: string,
  from: number,
  to: number,
  opts: ScanOptions,
): RegionScan {
  const tokens: WordSpan[] = [];
  const parens: number[] = [];
  const end = Math.min(to, text.length);
  let lastCodeEnd = from;
  let lastCodeChar = '';
  let i = from;
  const markCode = (at: number) => {
    lastCodeEnd = at;
    lastCodeChar = text[at - 1] ?? '';
  };
  while (i < end) {
    const ch = text[i] ?? '';
    const next = text[i + 1] ?? '';
    if (ch === '-' && next === '-') {
      while (i < end && text[i] !== '\n') i += 1;
      continue;
    }
    if (ch === '#' && opts.hashIsLineComment) {
      while (i < end && text[i] !== '\n') i += 1;
      continue;
    }
    if (ch === '$' && opts.dollarQuotedStrings && startsToken(text, i, from)) {
      const closed = dollarQuotedEnd(text, i, end);
      if (closed !== -1) {
        i = closed;
        markCode(i);
        continue;
      }
    }
    if (ch === '/' && next === '*') {
      i += 2;
      let depth = 1;
      while (i < end && depth > 0) {
        if (text[i] === '*' && text[i + 1] === '/') { depth -= 1; i += 2; continue; }
        if (opts.nestedBlockComments && text[i] === '/' && text[i + 1] === '*') {
          depth += 1;
          i += 2;
          continue;
        }
        i += 1;
      }
      continue;
    }
    if (ch === "'") {
      i += 1;
      while (i < end) {
        // MySQL (and PG's non-standard_conforming_strings) escape with a
        // backslash; elsewhere `\` is an ordinary character.
        if (opts.backslashEscapes && text[i] === '\\') { i += 2; continue; }
        if (text[i] === "'") break;
        i += 1;
      }
      i = Math.min(i + 1, end);
      markCode(i);
      continue;
    }
    if (ch === '"' || ch === '`' || ch === '[') {
      // SQL escapes the closing delimiter by doubling it: `[x]]y]`, `"a""b"`,
      // `` `a``b` ``. Closing at the first one leaked the rest as code.
      const close = ch === '[' ? ']' : ch;
      i += 1;
      while (i < end) {
        if (text[i] === close) {
          if (text[i + 1] === close) { i += 2; continue; }
          break;
        }
        i += 1;
      }
      i = Math.min(i + 1, end);
      markCode(i);
      continue;
    }
    if (IDENT_CHAR.test(ch)) {
      const tokenStart = i;
      while (i < end && IDENT_CHAR.test(text[i] ?? '')) i += 1;
      tokens.push({ word: text.slice(tokenStart, i).toUpperCase(), start: tokenStart });
      markCode(i);
      continue;
    }
    if (ch === '(') parens.push(i);
    else if (ch === ')') parens.pop();
    i += 1;
    if (!/\s/.test(ch)) markCode(i);
  }
  return {
    tokens,
    lastCodeEnd,
    lastCodeChar,
    parenDepth: parens.length,
    openParenAt: parens.length > 0 ? parens[parens.length - 1]! : null,
  };
}

/** True when the char at `at` begins a token — i.e. nothing but whitespace or a
 *  delimiter precedes it. Guards the dollar-quote check so an identifier that
 *  merely CONTAINS `$` (`v$session`, Oracle's `a$b$c`) is never read as one. */
function startsToken(text: string, at: number, from: number): boolean {
  if (at <= from) return true;
  const prev = text[at - 1] ?? '';
  return /[\s(,;=]/.test(prev);
}

/** End of a `$tag$ … $tag$` literal starting at `from`, or -1 when this `$` does
 *  not open one. An UNTERMINATED tag still opens a literal and runs to the end
 *  of the region — that is the shape while the user is typing one, and reading
 *  its contents as code let `SELECT $body$ UNION ▮` answer with set operators.
 *  (`startsToken` is what keeps identifiers containing `$` out of here.) */
function dollarQuotedEnd(text: string, from: number, to: number): number {
  let i = from + 1;
  while (i < to && /[A-Za-z0-9_]/.test(text[i] ?? '')) i += 1;
  if (i >= to || text[i] !== '$') return -1;
  const tag = text.slice(from, i + 1);
  const closeAt = text.indexOf(tag, i + 1);
  // A single `indexOf` per opener, and an unterminated one consumes the rest —
  // so a buffer of distinct unmatched tags cannot make this quadratic.
  if (closeAt === -1 || closeAt + tag.length > to) return to;
  return closeAt + tag.length;
}

/** Upper-cased barewords in the region, skipping comments, string literals and
 *  delimited identifiers. See `scanRegion`. */
export function barewordTokens(
  text: string,
  start: number,
  end: number,
  opts: ScanOptions = {},
): readonly WordSpan[] {
  return scanRegion(text, start, end, opts).tokens;
}

/**
 * End offset for a keyword-slot token scan, or null when the cursor is not at a
 * classifiable boundary. After whitespace the scan runs to the cursor; inside a
 * freshly-started word it stops at the word's start, so the partial token the
 * user is typing does not join the shape being matched.
 */
export function slotScanEnd(text: string, offset: number, statementStart: number): number | null {
  const end = Math.min(offset, text.length);
  if (end <= statementStart) return null;
  const prev = text[end - 1] ?? '';
  if (/\s/.test(prev)) return end;
  if (!IDENT_CHAR.test(prev)) return null;
  let j = end;
  while (j > statementStart && IDENT_CHAR.test(text[j - 1] ?? '')) j -= 1;
  // The partial word must START a token — a `.`/quote before it means we are in
  // a qualified reference, which is not a keyword position at all.
  if (j > statementStart && !/\s/.test(text[j - 1] ?? '')) return null;
  return j;
}

/** Net unclosed `(` count between `from` and `to`, ignoring parens inside
 *  comments and literals. */
export function openParenDepth(
  text: string,
  from: number,
  to: number,
  opts: ScanOptions = {},
): number {
  return scanRegion(text, from, to, opts).parenDepth;
}

/** The lexing rules for an engine. */
export function scanOptionsFor(engineId: string): ScanOptions {
  return {
    hashIsLineComment: engineId === 'mysql',
    dollarQuotedStrings: engineId === 'pgsql',
    nestedBlockComments: engineId === 'pgsql',
    backslashEscapes: engineId === 'mysql',
  };
}
